/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import FocusTrap from "focus-trap-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { AppContext } from "../../context";
import { BRANDS } from "../../configs";
import { TextButton } from "../Button";
import styles from "./ListView.scss";

const ListView = React.forwardRef(
	(
		{
			brand,
			reactDOM,
			appRoot,
			className,
			open,
			popperStyles,
			popperAttributes,
			children,
			onClose,
			...rest
		},
		ref
	) => {
		const [useAppState] = AppContext.useAppContext();
		const [containerWidth, setContainerWidth] = useState(appRoot?.clientWidth);
		const listViewRef = useRef();

		const unmountTrap = () => {
			onClose();
			return true;
		};

		useEffect(() => {
			const updateContainerSize = () => {
				setContainerWidth(appRoot?.clientWidth);
			};

			window.addEventListener("resize", updateContainerSize);

			if (listViewRef.current) {
				disableBodyScroll(listViewRef.current, {
					allowTouchMove: el => {
						let _el = el;
						while (_el && _el.parentElement) {
							if (_el.hasAttribute("body-scroll-lock-ignore")) {
								return true;
							}

							_el = _el.parentElement;
						}
						return false;
					}
				});
			}

			return () => {
				window.removeEventListener("resize", updateContainerSize);
				clearAllBodyScrollLocks();
			};
		}, [appRoot, listViewRef]);

		useEffect(() => {
			const handleDetectOutsideClick = e => {
				e.stopPropagation();
				e.stopImmediatePropagation();
				e.preventDefault();

				if (
					typeof e.composedPath === "function" &&
					!e.composedPath().includes(listViewRef.current)
				) {
					onClose();
				}
			};
			if (reactDOM !== document) {
				document.addEventListener("click", handleDetectOutsideClick);
			}

			return () => {
				if (reactDOM !== document) {
					document.removeEventListener("click", handleDetectOutsideClick);
				}
			};
		}, [reactDOM, listViewRef, onClose]);

		return (
			<FocusTrap
				active={open}
				dom={reactDOM}
				focusTrapOptions={{
					dom: reactDOM,
					allowOutsideClick: e => {
						// If clicked on same input
						if (e.target.closest("label")?.contains(listViewRef.current)) {
							return true;
						}

						return unmountTrap();
					},
					onDeactivate: unmountTrap
				}}>
				<div
					ref={ref}
					className={cx(styles.listView, { [styles[brand]]: styles[brand] })}
					{...popperAttributes?.popper}
					{...rest}
					style={{ width: containerWidth }}>
					<div
						ref={listViewRef}
						className={cx(
							className,
							styles.container,
							{ [styles[brand]]: styles[brand] },
							open && styles.open
						)}>
						<TextButton
							type="button"
							label={
								<>
									{useAppState.brand === BRANDS.VWQ &&
										useAppState.getDictionary("close")}
									<FontAwesomeIcon
										icon={faTimes}
										className={styles.closeIcon}
									/>
								</>
							}
							className={cx(styles.closeButton, {
								[styles[useAppState.brand]]: styles[useAppState.brand]
							})}
							onClick={unmountTrap}
						/>
						{children}
					</div>
				</div>
			</FocusTrap>
		);
	}
);

ListView.propTypes = {
	brand: PropTypes.oneOf(Object.keys(BRANDS)),
	reactDOM: PropTypes.instanceOf(DocumentFragment),
	open: PropTypes.bool,
	appRoot: PropTypes.any.isRequired,
	className: PropTypes.string,
	popperStyles: PropTypes.any,
	popperAttributes: PropTypes.any,
	children: PropTypes.oneOfType([
		PropTypes.node,
		PropTypes.arrayOf(PropTypes.node)
	]),
	onClose: PropTypes.func
};

ListView.defaultProps = {
	brand: BRANDS.SWG,
	reactDOM: document,
	open: false,
	className: undefined,
	popperStyles: undefined,
	popperAttributes: undefined,
	children: undefined,
	onClose: () => {}
};

export default ListView;
export { ListView };
